import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

// Floating particles effect - dust, ethereal smoke, mystical orbs
export const ParticlesBackground = () => {
  const containerRef = useRef(null);
  
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    // Create particles
    const particleCount = 50;
    const particles = [];
    
    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div');
      particle.className = 'particle';
      particle.style.left = `${Math.random() * 100}%`;
      particle.style.animationDelay = `${Math.random() * 15}s`;
      particle.style.animationDuration = `${15 + Math.random() * 15}s`;
      container.appendChild(particle);
      particles.push(particle);
    }
    
    // Create smoke elements
    const smokeCount = 5;
    for (let i = 0; i < smokeCount; i++) {
      const smoke = document.createElement('div');
      smoke.className = 'smoke';
      smoke.style.left = `${Math.random() * 100}%`;
      smoke.style.bottom = '-100px';
      smoke.style.animationDelay = `${Math.random() * 10}s`;
      smoke.style.animationDuration = `${25 + Math.random() * 15}s`;
      container.appendChild(smoke);
      particles.push(smoke);
    }
    
    return () => {
      particles.forEach(p => p.remove());
    };
  }, []);
  
  return <div ref={containerRef} className="particles-container" />;
};

// Babylonian symbols that float across the screen
export const FloatingSymbols = () => {
  const symbols = ['𒀭', '𒊹', '𒄑', '𒆠', '𒀀', '𒁹', '☽', '☉', '✧'];
  
  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
      {symbols.map((symbol, i) => (
        <motion.div
          key={i}
          className="absolute text-babylon-gold/10 text-4xl"
          initial={{ 
            x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
            y: -50,
            opacity: 0,
            rotate: 0
          }}
          animate={{ 
            y: typeof window !== 'undefined' ? window.innerHeight + 50 : 1000,
            opacity: [0, 0.3, 0.3, 0],
            rotate: 360
          }}
          transition={{
            duration: 20 + Math.random() * 20,
            repeat: Infinity,
            delay: i * 3,
            ease: "linear"
          }}
        >
          {symbol}
        </motion.div>
      ))}
    </div>
  );
};

// Hypnotic vortex background element
export const HypnoticVortex = ({ className = '' }) => {
  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      {/* Outer ring */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full border border-babylon-gold/5"
        animate={{ rotate: 360 }}
        transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
      />
      {/* Middle ring */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-babylon-amethyst/5"
        animate={{ rotate: -360 }}
        transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
      />
      {/* Inner ring */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full border border-babylon-blood/5"
        animate={{ rotate: 360 }}
        transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
      />
      {/* Core glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] h-[200px] rounded-full bg-gradient-radial from-babylon-gold/5 via-transparent to-transparent" />
    </div>
  );
};

// Cuneiform border decoration
export const CuneiformBorder = ({ children, className = '' }) => {
  return (
    <div className={`relative ${className}`}>
      {/* Top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-babylon-gold/50 to-transparent" />
      {/* Bottom border */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-babylon-gold/50 to-transparent" />
      {/* Corner symbols */}
      <span className="absolute top-2 left-2 text-babylon-gold/40 text-sm">𒀭</span>
      <span className="absolute top-2 right-2 text-babylon-gold/40 text-sm">𒊹</span>
      <span className="absolute bottom-2 left-2 text-babylon-gold/40 text-sm rotate-180">𒊹</span>
      <span className="absolute bottom-2 right-2 text-babylon-gold/40 text-sm rotate-180">𒀭</span>
      {children}
    </div>
  );
};

// Ishtar Star decoration
export const IshtarStar = ({ size = 'md', className = '', animated = true }) => {
  const sizes = {
    sm: 'w-6 h-6 text-lg',
    md: 'w-10 h-10 text-2xl',
    lg: 'w-16 h-16 text-4xl',
    xl: 'w-24 h-24 text-6xl'
  };
  
  return (
    <motion.div 
      className={`flex items-center justify-center ${sizes[size]} ${className}`}
      animate={animated ? { 
        rotate: [0, 360],
        scale: [1, 1.1, 1]
      } : {}}
      transition={{ 
        rotate: { duration: 20, repeat: Infinity, ease: "linear" },
        scale: { duration: 3, repeat: Infinity, ease: "easeInOut" }
      }}
    >
      <span className="text-babylon-gold drop-shadow-[0_0_10px_rgba(201,162,39,0.5)]">✧</span>
    </motion.div>
  );
};

// Winged Bull decoration (Lamassu)
export const WingedDecoration = ({ children }) => {
  return (
    <div className="relative inline-flex items-center gap-4">
      <span className="text-babylon-gold/50 text-xl">𒄑</span>
      {children}
      <span className="text-babylon-gold/50 text-xl scale-x-[-1]">𒄑</span>
    </div>
  );
};

// Serpent animated border
export const SerpentBorder = ({ children, className = '' }) => {
  return (
    <div className={`relative p-[2px] rounded-lg overflow-hidden ${className}`}>
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-babylon-gold via-babylon-serpent to-babylon-gold"
        animate={{ backgroundPosition: ['0% 50%', '200% 50%'] }}
        transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
        style={{ backgroundSize: '200% 100%' }}
      />
      <div className="relative bg-babylon-void rounded-lg">
        {children}
      </div>
    </div>
  );
};

export default ParticlesBackground;
